import { DisplayError } from "@/components/Error/Error";
import { Loading } from "@/components/Loading/Loading";
import { useOrg } from "@/hooks/org";
import { useGetOrgConsentStatusV1Query } from "@/store/clientApi";
import { selectRegion } from "@/store/slices/orgSlice";
import { RootState } from "@/store/store";
import { useAuth0 } from "@auth0/auth0-react";
import { FC, ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

interface VerifiedEmailGuardProps {
  redirectRoute?: string;
  children: ReactNode;
}

export const VerifiedEmailGuard: FC<VerifiedEmailGuardProps> = ({ redirectRoute = "/cases", children }) => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, error: authError } = useAuth0();

  // We need to fetch the org to determine the region,
  // which determines if we need to check for BAA acceptance as well as email verification.
  useOrg({ skip: true });

  const region = useSelector((state: RootState) => selectRegion(state));

  const isUS = region === "US";
  const {
    data: consentStatuses,
    isLoading: consentLoading,
    error: consentError
  } = useGetOrgConsentStatusV1Query(undefined, { skip: !isUS });

  const baaAccepted = consentStatuses?.find((s) => s.document.document_type === "baa")?.status === "accepted";
  const isLoading = authLoading || consentLoading || region === undefined;
  const needsRedirect = !isLoading && user != null && (!user.email_verified || (isUS && !baaAccepted));

  useEffect(() => {
    if (needsRedirect) {
      navigate(`/verify-email?target=${encodeURIComponent(redirectRoute)}`, {
        replace: true
      });
    }
  }, [needsRedirect, navigate, redirectRoute]);

  if (isLoading) {
    return <Loading />;
  }

  const error = authError || consentError;
  if (error) {
    return <DisplayError error={error} />;
  }

  if (needsRedirect) {
    return <Loading />;
  }

  return children;
};
